/**
 * The start dialog: pick where the questions come from (a set, or for
 * formats that can, a filter on the question bank), set the options the
 * module declares, create the session, go to its page.
 */
import {
  BANK_LIMITS,
  type BankCatalog,
  type BankFilter,
  type BankOrder,
  type GameInfo,
  type GenPicked,
  type SessionInfo,
} from '@schulspiele/protocol';

import { api, ApiError } from '@shared/api';
import {
  bindFilterFields,
  countPlayable,
  filterFieldsHtml,
  loadCatalog,
  readFilterFields,
} from '@shared/bank';
import { esc } from '@shared/dom';
import { t } from '@shared/i18n/de';
import { navigate } from '@shared/router';
import { openDialog } from '@shared/ui/dialog';
import { bindOptionFields, readOptionValues, renderOptionFields } from '@shared/ui/fields';
import { toastError } from '@shared/ui/toast';
import { openExamples } from './examples-dialog';
import { loadGameClient } from '../../games';

interface SetSummary {
  id: string;
  title: string;
  updatedAt: number;
  complete: boolean;
  /** For a format that plays other sets: questions it can play, of all. */
  playable?: number;
  total?: number;
}

export interface StartPreset {
  setId?: string;
  /** Start from the question bank with this filter. */
  bank?: BankFilter;
  /** Start with questions the Quiz-Generator chose (ADR 0020), described by `pickedLabel`. */
  picked?: GenPicked;
  pickedLabel?: string;
}

/** What a game's bank items are called (U19): Erklär es plays terms, Fakt oder Fake cards. */
const NOUNS: Record<string, [string, string]> = {
  erklaeres: ['Begriff', 'Begriffe'],
  faktfake: ['Karte', 'Karten'],
};

export async function openStartDialog(gameId: string, preset: StartPreset = {}): Promise<void> {
  let game: GameInfo;
  try {
    game = (await api<{ game: GameInfo }>(`/api/games/${encodeURIComponent(gameId)}`)).game;
  } catch (err) {
    toastError(err, t('format.notFound'));
    return;
  }
  const client = await loadGameClient(gameId);
  const fields = client?.optionFields ?? [];
  // Its own sets, or the sets of the formats it plays (ADR 0016: 1, 2 oder 3 plays Blitzquiz sets).
  const setGames = game.hasSets ? [gameId] : game.playsSetsOf;
  const usesSets = setGames.length > 0;
  const sets = (
    await Promise.all(
      setGames.map(
        async (id) =>
          (
            await api<{ sets: SetSummary[] }>(
              `/api/sets?game=${encodeURIComponent(id)}${game.hasSets ? '' : `&for=${encodeURIComponent(gameId)}`}`,
            )
          ).sets,
      ),
    )
  ).flat();
  const picked = preset.picked;
  const bankStart = game.bank?.start === true && picked === undefined;
  let catalog: BankCatalog | null = null;
  if (bankStart) {
    try {
      catalog = await loadCatalog();
    } catch {
      catalog = null;
    }
  }

  // "Aus einem Blitzquiz-Set": name the formats whose sets these are.
  const setLabel = game.hasSets
    ? 'Aus einem Fragenset'
    : `Aus einem ${(
        await Promise.all(
          setGames.map(async (id) =>
            api<{ game: GameInfo }>(`/api/games/${encodeURIComponent(id)}`)
              .then((r) => r.game.name)
              .catch(() => id),
          ),
        )
      ).join('- oder ')}-Set`;
  // Drafts cannot start; nor can a set with no question this format plays.
  const playable = sets.filter((s) => s.complete && s.playable !== 0);
  const unplayable = sets.filter((s) => s.complete && s.playable === 0).length;
  if (usesSets && playable.length === 0 && catalog === null && picked === undefined) {
    const dialog = openDialog({
      title: game.name,
      body: `<p>${sets.length === 0 ? t('start.noSets') : unplayable > 0 ? `In keinem Set gibt es Fragen, die ${esc(game.name)} spielen kann.` : t('start.onlyDrafts')}</p>`,
      // No dead end: a template or the Quiz-Generator are ways in too.
      actions: [
        { label: t('teacher.cancel'), value: 'cancel', kind: 'ghost' },
        ...(game.hasExamples && game.hasSets
          ? [{ label: t('library.fromExample'), value: 'example', kind: 'ghost' as const }]
          : []),
        ...(game.bank?.generate != null
          ? [{ label: 'Quiz-Generator', value: 'generator', kind: 'ghost' as const }]
          : []),
        { label: t('library.new'), value: 'new' },
      ],
    });
    const choice = await dialog.result;
    // The next page must not sit behind a closing modal (it would be inert).
    if (choice !== null) await dialog.gone;
    if (choice === 'new')
      navigate(`/lehrer/sets/neu?spiel=${encodeURIComponent(setGames[0] ?? gameId)}`);
    else if (choice === 'example') void openExamples(gameId);
    else if (choice === 'generator') navigate('/lehrer/generator');
    return;
  }

  // A format that also plays without questions starts "Ohne Fachfragen" (Werwolf).
  const optional = game.bank?.optional === true && !usesSets;
  const noun = NOUNS[gameId] ?? ['Frage', 'Fragen'];
  // Werwolf's Wissenswerwolf comes preselected (Max, U16).
  const fromBank = catalog !== null && (preset.bank !== undefined || playable.length === 0);
  const setSelect = usesSets
    ? playable.length === 0
      ? `<p class="muted">${sets.length === 0 ? 'Noch kein Fragenset.' : unplayable > 0 ? `Kein Set mit Fragen für ${esc(game.name)}.` : 'Es gibt nur Entwürfe.'}</p>`
      : // Drafts cannot start: they are not offered (the library shows them).
        `<label class="field">${t('start.set')}<select name="__set">${playable
          .map(
            (s) =>
              `<option value="${esc(s.id)}" ${s.id === preset.setId ? 'selected' : ''}>${esc(s.title || t('library.untitled'))}${
                s.playable !== undefined && s.total !== undefined && s.playable < s.total
                  ? ` (${String(s.playable)} von ${String(s.total)} Fragen spielbar)`
                  : ''
              }</option>`,
          )
          .join('')}</select></label>${
          playable.length < sets.length
            ? `<p class="muted">${hiddenNote(sets.length - playable.length - unplayable, unplayable, game.name)}</p>`
            : ''
        }`
    : '';

  // A format that needs many questions draws its own number (ADR 0018).
  const fixedDraw = game.bank?.draw ?? null;
  const minimum = game.bank?.min ?? null;
  const bankPart =
    catalog === null
      ? ''
      : `<div class="start-bank" data-from="bank" ${fromBank ? '' : 'hidden'}>
          ${filterFieldsHtml(catalog, preset.bank ?? {}, { sensitive: true })}
          ${
            fixedDraw !== null
              ? ''
              : `<div class="bank-filter__range">
            <label class="field">Anzahl ${noun[1]}<input type="number" name="__count" min="1" max="${String(BANK_LIMITS.maxDraw)}" value="10" inputmode="numeric" /></label>
            ${
              // Fakt oder Fake has its own "Karten mischen" setting.
              gameId === 'faktfake'
                ? ''
                : `<label class="field">Reihenfolge<select name="__order">
              <option value="difficulty">Leicht nach schwer</option>
              <option value="random">Zufällig</option>
            </select></label>`
            }
          </div>`
          }
          <p class="start-bank__count muted" data-count aria-live="polite"></p>
        </div>`;

  const sourceChoice =
    picked !== undefined
      ? `<p class="start-picked">${esc(preset.pickedLabel ?? `${String(picked.ids.length)} Fragen aus dem Quiz-Generator`)}</p>`
      : catalog === null
        ? setSelect
        : `<fieldset class="start-source">
          <legend>${noun[1]}</legend>
          ${
            optional
              ? `<label class="start-source__pick"><input type="radio" name="__source" value="none" ${fromBank ? '' : 'checked'} /><span>Ohne Fachfragen</span></label>`
              : `<label class="start-source__pick"><input type="radio" name="__source" value="set" ${fromBank ? '' : 'checked'} ${playable.length === 0 ? 'disabled' : ''} /><span>${esc(setLabel)}</span></label>`
          }
          <label class="start-source__pick"><input type="radio" name="__source" value="bank" ${fromBank ? 'checked' : ''} /><span>${optional ? 'Mit Fachfragen aus der Bibliothek' : 'Aus der Fragenbibliothek'}</span></label>
        </fieldset>
        <div data-from="set" ${fromBank ? 'hidden' : ''}>${setSelect}</div>
        ${bankPart}`;

  const body = `
    ${sourceChoice}
    ${renderOptionFields(fields)}
    ${fields.length === 0 && !usesSets ? `<p class="muted">${t('start.noOptions')}</p>` : ''}
    <ul class="issues" data-issues hidden></ul>`;

  const dialog = openDialog({
    title: `${game.name} ${t('start.titleSuffix')}`,
    body,
    ...(fields.some((f) => f.kind === 'teams') || catalog !== null
      ? { className: 'dialog--wide' }
      : {}),
    actions: [
      { label: t('teacher.cancel'), value: 'cancel', kind: 'ghost' },
      { label: t('teacher.start'), value: 'start' },
    ],
    async onSubmit(form) {
      const options = readOptionValues(form, fields);
      const data = new FormData(form);
      const source = data.get('__source') ?? 'set';
      let payload: Record<string, unknown> = { gameId, options };
      if (picked !== undefined) {
        payload = { ...payload, picked };
      } else if (source === 'bank') {
        // With a fixed draw the server takes its own number; this one only passes the schema.
        const count = Math.max(
          1,
          Math.min(BANK_LIMITS.maxDraw, Math.trunc(Number(data.get('__count')) || 10)),
        );
        payload = {
          ...payload,
          bank: {
            filter: readFilterFields(form, { sensitive: true }),
            count,
            order: (data.get('__order') === 'random' ? 'random' : 'difficulty') satisfies BankOrder,
          },
        };
      } else if (usesSets) {
        const setId = data.get('__set');
        if (typeof setId === 'string') payload = { ...payload, setId };
      }
      try {
        const session = await api<SessionInfo>('/api/sessions', { method: 'POST', body: payload });
        navigate(`/lehrer/session/${session.id}`);
        return true;
      } catch (err) {
        const issues = form.querySelector<HTMLElement>('[data-issues]');
        const bodyErr =
          err instanceof ApiError
            ? (err.body as { reason?: string; issues?: { message: string }[] } | null)
            : null;
        const messages = bodyErr?.issues?.map((i) => i.message) ?? [
          bodyErr?.reason ?? t('error.generic'),
        ];
        if (issues !== null) {
          issues.hidden = false;
          issues.innerHTML = messages.map((m) => `<li>${esc(m)}</li>`).join('');
        }
        return false;
      }
    },
  });
  bindOptionFields(dialog.form, fields);

  if (catalog !== null) {
    const form = dialog.form;
    const types = game.bank?.types ?? [];
    const countLine = form.querySelector<HTMLElement>('[data-count]');
    let seq = 0;
    const refresh = (): void => {
      if (countLine === null) return;
      const mine = (seq += 1);
      countPlayable(readFilterFields(form, { sensitive: true }), types, gameId)
        .then((n) => {
          if (mine !== seq) return;
          const want =
            fixedDraw ??
            (Number(form.querySelector<HTMLInputElement>('[name="__count"]')?.value ?? '10') || 10);
          const few = minimum !== null && n < minimum;
          const found = `${n.toLocaleString('de-DE')} passende ${n === 1 ? noun[0] : noun[1]}`;
          // An empty filter that still has too few: the library itself is too small.
          const f = readFilterFields(form, { sensitive: true });
          const open =
            (f.levels ?? []).length === 0 &&
            f.subject === undefined &&
            f.topic === undefined &&
            f.difficultyMin === undefined &&
            f.difficultyMax === undefined;
          countLine.textContent =
            n === 0
              ? `Zu diesem Filter gibt es keine spielbaren ${noun[1]}.`
              : few
                ? `${found}. Das Spiel braucht mindestens ${String(minimum)}: ${open ? 'Die Bibliothek hat noch zu wenige.' : 'Wähle einen größeren Filter.'}`
                : fixedDraw !== null
                  ? `${found}, das Spiel zieht bis zu ${String(fixedDraw)} (keine doppelt).`
                  : `${found}${n < want ? `: es werden ${String(n)} gespielt` : ''}.`;
          countLine.classList.toggle('is-empty', n === 0 || few);
        })
        .catch(() => {
          if (mine === seq) countLine.textContent = '';
        });
    };
    bindFilterFields(form, catalog, refresh, { sensitive: true });
    form.querySelector('[name="__count"]')?.addEventListener('input', refresh);
    form.addEventListener('change', (e) => {
      const el = e.target as HTMLInputElement;
      if (el.name !== '__source') return;
      for (const part of form.querySelectorAll<HTMLElement>('[data-from]')) {
        part.hidden = part.dataset.from !== el.value;
      }
      if (el.value === 'bank') refresh();
    });
    if (fromBank) refresh();
  }
  await dialog.result;
}

/** "2 Entwürfe und 1 Set ohne passende Fragen nicht angezeigt." */
function hiddenNote(drafts: number, unplayable: number, name: string): string {
  const parts: string[] = [];
  if (drafts > 0) parts.push(`${String(drafts)} ${drafts === 1 ? 'Entwurf' : 'Entwürfe'}`);
  if (unplayable > 0)
    parts.push(
      `${String(unplayable)} ${unplayable === 1 ? 'Set' : 'Sets'} ohne Fragen für ${name}`,
    );
  return `${parts.join(' und ')} nicht angezeigt.`;
}
